<script setup lang="ts" generic="TData extends Record<string, unknown>">
    import { getConfig, klinikenApi } from "@/api";
    import KButton from "@/components/Button.vue";
    import { CellContentWidth, CellPadding } from "@/components/KTable/helpers";
    import KCell from "@/components/KTable/KCell.vue";
    import { Column, KTableAction, TableValue, TableValues } from "@/components/KTable/types";
    import KlinikenDropdownMenu from "@/components/Table/KlinikenDropdownMenu.vue";
    import { useStore } from "@/store";
    import { flashMessage } from "@/utils";
    // @ts-ignore
    import { Draggable } from "@fullcalendar/interaction";
    import { computed, nextTick, onBeforeMount, onMounted, onUnmounted, ref, toRefs, watch } from "vue";

    const props = withDefaults(
        defineProps<{
            /**
             * Det som ska renderas i tabellen. Kan vara en array med objekt eller funktion som returnerar
             * objekt. key är namnet på kolumnen som ska rendera viss data. value är antingen ett objekt som kommer
             * skickas in i column template eller string | number som renderas direkt.
             * Funktionen kan användas för att göra hämtningar och lyssnar på store per rad istället
             * för att hämta och sätta allt i parent.
             *
             */
            value: TableValues;
            columns: Array<Column>;
            theme?: "default" | "grey";
            /**
             * Namn på unikt värde som kommer användas som Id på varje rad. Default: id
             */
            dataKey?: string;
            /**
             * Antal rader per sida
             */
            rows?: number;
            /**
             * Index på raden som ska renderas högst upp
             */
            first?: number;
            /**
             * Total mängd rader. Default är length på value
             */
            totalRecords?: number;
            /**
             * Ska paginering visas?
             */
            paginator?: boolean;
            /**
             * Placering av paginator
             */
            paginatorPosition?: "top" | "bottom" | "both";
            /**
             * Ska paginering alltid visas, även om det bara är 1 sida?
             */
            alwaysShowPaginator?: boolean;
            /**
             * Mängd klickbara sidor som ska visas i taget
             */
            pageLinkSize?: number;
            lazy?: boolean;
            loading?: boolean;
            /**
             * Ikon som ska visas vid laddning. Default sätts internt
             */
            loadingIcon?: string;
            sortField?: string | (() => { [key: string]: unknown });
            /**
             * Ordning på sorteringen
             */
            sortOrder?: number;
            defaultSortOrder?: number;
            multiSortMeta?: Array<{ [key: string]: unknown }>;
            /**
             * Inställning för att sortera på 1 eller fler kolumner
             */
            sortMode?: "single" | "multiple";
            filters?: Array<{
                [key: string]: unknown;
            }>;
            /**
             * En eller flera valda rader
             */
            selection?: TableValue | TableValue[];
            /**
             * Inställning för att kunna välja 1 eller fler kolumner
             */
            selectionMode?: "single" | "multiple" | "none";
            /**
             * Ska bredden på kolumner kunna justeras av användare?
             */
            resizableColumns?: boolean;
            /**
             * Inställning på hur kolumner ska bre ut sig
             */
            columnResizeMode?: "fit" | "expand";
            /**
             * Ska kolumner kunna dras och manuellt sorteras av användare?
             */
            reorderableColumns?: boolean;
            /**
             * Ska rader kunna expanderas av användaren?
             */
            expandableRows?: boolean;
            /**
             * Ska minimera/maximera alla-knapparna visas?
             */
            expandToggles?: boolean;
            /**
             * Actions som kommer vara tillgängliga på alla rader.
             * Overridas av actions som skickas in per rad.
             */
            actions?: KTableAction<TData>[];
            /**
             * Ska checkbox/radio button synas när raderna är valbara?
             */
            showSelectionToggle?: boolean;
            /**
             * Ska det gå att exportera ut tabellen till CSV?
             */
            exportable?: boolean;
            /**
             * Ska det gå att välja synliga/dolda kolumner?
             */
            togglableColumns?: boolean;
            /**
             * Ska expanderbara rader expanderas/döljas vid klick var som helst på raden? Standard är att chevron
             * måste klickas
             */
            expandOnRowClick?: boolean;
            /**
             * Ska fler rader kunna vara expanderade samtidigt?
             */
            multipleExpandableRows?: boolean;
            /**
             * Ska chevron för expandering visas?
             */
            showExpandToggle?: boolean;
            /**
             * Variant för expanderade raden. full-framed innebär att den tar hela bredden. default är att
             * innehållet alignas med innehållet i kolumnerna
             */
            expandedRowVariant?: "default" | "full-framed";
            /**
             * Bredd på tabellen
             * default: anpassar sig efter content i tabellen
             * unlimited: 100%
             * max-content: $content-max-width
             */
            width?: "default" | "unlimited" | "max-content";
            /**
             * Meddelande som visar medan tabellen laddar
             */
            loadingMessage?: string;
            /**
             * Funktion som anropas med data för en rad och returnerar namn på variant (styling) av en rad
             */
            getRowVariant?: (args: { data: TableValue }) => "default" | "warning";
            /**
             * Mängden av rader som kan väljas att visas i taget av användaren
             */
            rowsPerPageOptions?: number[];
            /**
             * Används bland annat för key för state. Måste vara unikt namn bland tabeller för att state management
             * ska fungera
             */
            name: string;
            /**
             * Antal rader som ska visas på varje sida om tabellen är paginerad
             */
            defaultRowsPerPage?: number;
            /**
             * Ska valda rader kunna avväljas? Sätt till true om selectionMode är single och val av rad används
             * för visuellt syfte, för att undvika att raden avväljs när den klickas på igen
             */
            unselectable?: boolean;
            /**
             * Ska inställningar (ordning på kolumner, vilka kolumner som är synliga) för tabellen lagras?
             */
            useState?: boolean;
        }>(),
        {
            theme: "default",
            first: 0,
            paginator: false,
            paginatorPosition: "bottom",
            alwaysShowPaginator: false,
            pageLinkSize: 5,
            lazy: true,
            loading: undefined,
            defaultSortOrder: 1,
            sortMode: "single",
            selectionMode: "single",
            columnResizeMode: "expand",
            expandableRows: false,
            expandToggles: false,
            dataKey: "id",
            showSelectionToggle: false,
            exportable: false,
            togglableColumns: false,
            expandOnRowClick: false,
            multipleExpandableRows: true,
            showExpandToggle: true,
            expandedRowVariant: "default",
            width: "default",
            loadingMessage: "",
            rowsPerPageOptions: () => [20, 50, 100],
            defaultRowsPerPage: 20,
            useState: false,
        }
    );

    const emit = defineEmits<{
        /**
         * Vid ändring av sida
         */
        (
            event: "page",
            payload: {
                originalEvent: Event;
                /**
                 * Nya page
                 */
                page: number;
                /**
                 * Totala mängden pages
                 */
                pageCount: number;
                /**
                 * Index på första raden
                 */
                first: number;
                /**
                 * Mängden rader i nya page
                 */
                rows: number;
                sortField: unknown;
                /**
                 * Ordning på sorteringen
                 */
                sortOrder: number;
                multiSortMeta: unknown;
                filters: unknown;
                filterMatchModes: unknown;
            }
        ): void;
        (event: "selected", payload: { value?: TableValue[] | TableValue }): void;
        (
            event: "rowClick",
            payload: {
                data: TableValue;
            }
        ): void;
        (
            event: "menuClick",
            payload: {
                data: TableValue;
            }
        ): void;
        (
            event: "sort",
            payload: {
                originalEvent: Event;
                first: number;
                rows: number;
                sortField: string;
                sortOrder: number;
            }
        ): void;
    }>();

    const {
        value,
        selectionMode,
        dataKey,
        actions,
        multipleExpandableRows,
        expandableRows,
        expandOnRowClick,
        columns,
        loading,
        loadingMessage,
        getRowVariant,
        reorderableColumns,
        name,
        unselectable,
        defaultRowsPerPage,
        first,
        paginatorPosition,
    } = toRefs(props);
    /**
     * Alla värden (rader)
     */
    const allValues = ref(Array.isArray(value.value) ? value.value : []);
    /**
     * Valda värden (rader)
     */
    const selectedValues = ref<TableValue[] | TableValue | undefined>(
        selectionMode.value === "single" || selectionMode.value === "none" ? undefined : []
    );
    /**
     * Expanderade rader
     */
    const expandedRows = ref<TableValue[]>([]);
    const tableRef = ref();
    /**
     * Mängd padding på celler
     */
    const padding = ref<string>(CellPadding);
    /**
     * Synliga kolumner
     */
    const toggledColumns = ref<string[]>([]);
    /**
     * Intern boolean som säger om tabellens value håller på att hämtas. Sätts om inskickade value är en funktion
     */
    const internalLoading = ref(false);
    const toggledColumnsAutocomplete = ref();
    /**
     * Bredd på checkbox/radio button på valbara rader
     */
    const selectToggleWidth = CellContentWidth.icon;

    /**
     * Filtrerade kolumner baserat på vilka kolumner som ska vara synliga
     */
    const filteredColumns = computed(() => {
        return columns.value.filter((column) => {
            return (
                !toggledColumns.value.length ||
                (toggledColumns.value.includes(column.field) && (!column.shouldRender || column.shouldRender()))
            );
        });
    });

    const headCursor = computed(() => (reorderableColumns?.value ? "grab" : "default"));

    defineExpose({
        expandedRows,
    });

    /**
     * Expandera alla rader
     */
    const expandAll = () => {
        expandedRows.value = allValues.value as any;
    };

    /**
     * Minimera alla rader
     */
    const collapseAll = () => {
        expandedRows.value = [];
    };

    /**
     * Triggras när specifikt chevron klickas på
     */
    const onRowCollapse = ({ data }: { data: TableValue }) => {
        if (expandableRows?.value) {
            const modifiedExpanded = [...(expandedRows.value ?? [])];
            const existingExpandedIndex = modifiedExpanded.findIndex((expanded) => expanded.id === data.id);

            if (existingExpandedIndex > -1) {
                modifiedExpanded.splice(existingExpandedIndex, 1);

                expandedRows.value = modifiedExpanded;
            }
        }
    };

    /**
     * Triggras när specifikt chevron klickas på
     */
    const onRowExpand = ({ data }: { data: TableValue }) => {
        if (!multipleExpandableRows?.value) {
            expandedRows.value = [data];
        }

        emit("rowClick", { data });
    };

    /**
     * Triggras när dropdown (meny) togglas
     */
    const onToggleDropdown = ({ item }: { item: unknown }) => {
        emit("menuClick", { data: item as TableValue });
    };

    /**
     * Expandera rad
     */
    const expandRow = ({ data }: { data: TableValue }) => {
        if (expandedRows.value && expandOnRowClick.value) {
            // Om bara 1 rad ska tillåtas att expanderas i taget
            if (!multipleExpandableRows.value) {
                // Stänger expanderade raden om den redan är expanderad
                expandedRows.value = expandedRows.value.find(
                    (row) => (row as { id: string }).id === (data as { id: string }).id
                )
                    ? []
                    : [data];
            }
        }
    };

    /**
     * Vid klick på rad
     */
    const onRowClick = ({ data }: { data: TableValue }) => {
        emit("rowClick", { data });
    };

    /**
     * Vid export
     */
    const exportCsv = () => {
        tableRef.value.exportCSV();
        flashMessage("CSV exporterad!");
    };

    /**
     * Check för att se om raden har några actions. Används för att avgöra om meny och knapp ska visas
     */
    const hasActions = () => {
        return actions?.value || allValues?.value?.find((singleValue) => (singleValue as any).actions);
    };

    /**
     * Skickar inställningar för tabell till BE och sätter dem lokalt på current profile
     */
    const saveTableSettings = async () => {
        const store = useStore();

        if (
            sessionStorage.getItem(name.value) !==
                store.getters["userData/currentProfile"].table_settings?.[name.value] ||
            sessionStorage.getItem(`${name.value}-custom`) !==
                store.getters["userData/currentProfile"].table_settings?.[`${name.value}-custom`]
        ) {
            try {
                const { data } = await klinikenApi.patch<{ table_settings: { [key: string]: string } }>(
                    `/core/profiles/current/`,
                    {
                        table_settings: {
                            ...Object.fromEntries(
                                // Hämta bara tabellinställningar
                                Object.entries(sessionStorage)
                                    .filter(([key]) => {
                                        return key.endsWith("-table") || key.endsWith("-table-custom");
                                    })
                                    .map(([key, value]) => {
                                        // Rensa bort värden som inte ska sparass
                                        const {
                                            first,
                                            multiSortMeta,
                                            expandedRows,
                                            expandedRowKeys,
                                            sortField,
                                            sortOrder,
                                            ...settings
                                        } = JSON.parse(value);

                                        return [key, JSON.stringify(settings)];
                                    })
                            ),
                        },
                    },
                    getConfig({})
                );

                store.commit("userData/setCurrentProfileTableSettings", data.table_settings);
            } catch (error) {
                console.error(error);
            }
        }
    };

    /**
     * När en kolumn togglas (visas/döljs)
     */
    const onToggleColumn = async ({ value }: { value: string[] }) => {
        toggledColumns.value = value;
    };

    /**
     * När autocomplete för toggle av kolumner tappar fokus
     */
    const onToggleColumnBlur = async () => {
        saveTableSettings();
    };

    /**
     * När kolumner flyttas runt. Tvingad ordning görs i Primevue för att se till att frysta kolumner alltid
     * hamnar längst till vänster/höger
     */
    const onColumnReorder = async () => {
        const leftColumns: string[] = [];
        const centerColumns: string[] = [];
        const rightColumns: string[] = [];

        columns.value.forEach((column) => {
            if (column.frozen && (!column.alignFrozen || column.alignFrozen === "left")) {
                leftColumns.push(column.field);
            } else if (column.frozen && column.alignFrozen === "right") {
                rightColumns.push(column.field);
            } else {
                centerColumns.push(column.field);
            }
        });

        // Vi behöver overrida sättningen av column order för att se till att frysta kolumner alltid hamnar längst bort
        // till höger/vänster. Primevue tillåter annars att andra kolumner kan dras till höger/vänster om frysta
        tableRef.value.d_columnOrder = [
            ...(tableRef.value.d_columnOrder as string[]).filter((field) => leftColumns.includes(field)),
            ...(tableRef.value.d_columnOrder as string[]).filter((field) => centerColumns.includes(field)),
            ...(tableRef.value.d_columnOrder as string[]).filter((field) => rightColumns.includes(field)),
        ];

        // Behövs för att nya ordningen av kolumner ska hinnas lagras i sessionstorage
        await nextTick();
        saveTableSettings();
    };

    /**
     * Vid val av rad
     */
    const onRowSelect = ({ data }: { data: TableValue }) => {
        if (selectionMode.value === "single") {
            (selectedValues.value as unknown as TableValue) = data;
        } else if (
            selectionMode.value === "multiple" &&
            !(selectedValues.value as unknown as TableValue[])?.find?.((value) => value.id === data.id)
        ) {
            selectedValues.value = [...(selectedValues.value as unknown as TableValue[]), data];
        }

        expandRow({ data });
    };

    /**
     * Vid val av redan vald rad
     */
    const onRowUnselect = ({ data }: { data: TableValue }) => {
        if (!unselectable?.value) {
            if (selectionMode.value === "single") {
                selectedValues.value = undefined;
            } else if (selectionMode.value === "multiple") {
                const modifiedSelectedValues = [...(selectedValues.value as unknown as TableValue[])];

                modifiedSelectedValues.splice(
                    modifiedSelectedValues.findIndex((value) => value.id === data.id),
                    1
                );

                selectedValues.value = modifiedSelectedValues;
            }
        }

        expandRow({ data });
    };

    /**
     * Returnerar info om kolumn som sorterats på.
     */
    const onSort = (props: {
        originalEvent: Event;
        first: number;
        rows: number;
        sortField: string;
        sortOrder: number;
        multiSortMeta?: string[];
        expandedRows?: any[];
        expandedRowKeys?: string[];
    }) => {
        emit("sort", props);
    };
    const getWidth = ({ columns, index }: { columns: Column[]; index: number }) => {
        const column = columns[index];

        if ((columns[index - 1]?.frozen && !column.frozen) || (columns[index + 1]?.frozen && !column.frozen)) {
            return `calc(${column.minWidth ?? CellContentWidth.default} + ${padding.value})`;
        }

        return column.minWidth ?? CellContentWidth.default;
    };

    const internalGetRowClasses = (data: TableValue) => {
        let classes = `variant-${getRowVariant?.value?.({ data }) ?? "default"}`;

        if (data.tableInfo?.isNewlyUpdated) {
            classes = `${classes} newly-updated`;
        }

        if (data.tableInfo?.isNewlyCreated) {
            classes = `${classes} newly-created`;
        }

        return classes;
    };

    /**
     * Sätter toggled options vid mount på tabellen och autocomplete. Hämtar från state eller
     * skapar nytt state
     */
    const setToggledOptions = () => {
        const storageItem = sessionStorage.getItem(`${name.value}-custom`);

        if (storageItem) {
            const state = JSON.parse(storageItem);

            if (state.toggledColumns) {
                toggledColumns.value = state.toggledColumns;
            } else {
                state.toggledColumns = toggledColumns.value;

                sessionStorage.setItem(`${name.value}-custom`, JSON.stringify(state));
            }
        } else {
            resetToggledColumns();
        }

        toggledColumnsAutocomplete?.value?.setOptions?.({
            setDefault: true,
            setSelectedValues: true,
            options: columns.value
                .filter((column) => !column.shouldRender || column.shouldRender())
                .map((column) => ({
                    value: column.field,
                    label: column.header ?? column.field,
                    selected: toggledColumns.value.includes(column.field),
                })),
        });
    };

    const setupDraggable = () => {
        new Draggable(tableRef.value.$el, {
            itemSelector: ".draggable",
        });
    };

    /**
     * När sida som ska visas eller antal rader per sida som ska visas ändras. (Innehåller mer, men vi använder
     * oss inte av resten)
     */
    const onPage = ({
        page,
        ...otherProps
    }: {
        originalEvent: Event;
        page: number;
        pageCount: number;
        first: number;
        rows: number;
        sortField: unknown;
        sortOrder: number;
        multiSortMeta: unknown;
        filters: unknown;
        filterMatchModes: unknown;
    }) => {
        emit("page", { page: page + 1, ...otherProps });
    };

    /**
     * Anropas när ändring görs på antal rader som ska visas per sida
     */
    const onRowsPerPage = async () => {
        await nextTick();

        saveTableSettings();
    };

    const getRowsPerPage = () => {
        return JSON.parse(sessionStorage.getItem(name.value) ?? "{}")?.rows ?? defaultRowsPerPage.value;
    };

    const resetToggledColumns = () => {
        toggledColumns.value = columns.value.map((column) => column.field);

        sessionStorage.setItem(`${name.value}-custom`, JSON.stringify({ toggledColumns: toggledColumns.value }));
    };

    const resetColumnsOrder = () => {
        tableRef.value.d_columnOrder = columns.value.map((column) => column.field);
    };

    const resetColumns = () => {
        resetToggledColumns();
        setToggledOptions();
        resetColumnsOrder();
    };

    const removeUnwantedSettings = () => {
        const settings = JSON.parse(sessionStorage.getItem(name.value) ?? "{}");

        const { first, multiSortMeta, expandedRows, expandedRowKeys, sortField, sortOrder, ...all } = settings;

        sessionStorage.setItem(name.value, JSON.stringify(all));
    };

    onBeforeMount(async () => {
        if (Array.isArray(value.value)) {
            // Sätter värden rakt av om det är en array
            allValues.value = value.value;
        } else if (typeof value.value === "function") {
            // Anropar funktion om inskickade value är en funktion
            try {
                internalLoading.value = true;

                allValues.value = await value.value();
            } catch (error) {
                console.error(error);

                allValues.value = [];
            } finally {
                internalLoading.value = false;
            }
        }
    });

    onMounted(async () => {
        if (props.togglableColumns) {
            setToggledOptions();
        } else {
            toggledColumns.value = columns.value.map((column) => column.field);
        }

        // Renderingsfix. Låt stå
        await nextTick();

        setupDraggable();
    });

    onUnmounted(() => {
        removeUnwantedSettings();
    });

    /**
     * Vid ändring av vald/valda rad(er)
     */
    watch(selectedValues, (newValue) => {
        emit("selected", { value: newValue });
    });

    /**
     * Sätter om värden om propen förändras
     */
    watch(value, async (newValue) => {
        if (Array.isArray(newValue)) {
            allValues.value = newValue;
        } else if (typeof value.value === "function") {
            try {
                allValues.value = await value.value();
            } catch (error) {
                console.error(error);

                allValues.value = [];
            }
        }
    });

    watch(toggledColumns, (newValue, oldValue) => {
        if (oldValue.length) {
            const storageItem = sessionStorage.getItem(`${name.value}-custom`);

            if (storageItem) {
                const customState = JSON.parse(storageItem);

                customState.toggledColumns = newValue;

                sessionStorage.setItem(`${name.value}-custom`, JSON.stringify(customState));
            } else {
                sessionStorage.setItem(
                    `${name.value}-custom`,
                    JSON.stringify({
                        toggledColumns: newValue,
                    })
                );
            }
        }
    });

    // Primevues paginator uppdateras inte automatiskt när first ändrar värde. Workaround för det
    watch(first, (newValue) => {
        if (paginatorPosition.value === "bottom") {
            tableRef.value?.allChildren?.[tableRef.value.allChildren.length - 1]?.changePage?.(newValue);
        }
    });
</script>

<template>
    <div :class="['k-table', `width-${width}`, { empty: totalRecords == 0 }]">
        <PrimeDataTable
            v-bind="{ ...$props, ...$attrs }"
            lazy
            removableSort
            ref="tableRef"
            :value="allValues"
            :expandedRows.sync="expandedRows"
            :selection="selectedValues"
            :rowClass="internalGetRowClasses"
            @row-expand="onRowExpand"
            @row-collapse="onRowCollapse"
            @column-reorder="onColumnReorder"
            @row-click="onRowClick"
            @row-select="onRowSelect"
            @row-unselect="onRowUnselect"
            :selectionMode="selectionMode === 'none' ? undefined : selectionMode"
            :dataKey="dataKey"
            :metaKeySelection="false"
            scrollable
            scrollHeight="flex"
            :reorderableColumns="reorderableColumns"
            :class="[`expanded-variant-${expandedRowVariant}`, `theme-${theme}`]"
            :loading="typeof loading === 'boolean' ? loading : internalLoading"
            loadingIcon=""
            currentPageReportTemplate="Visar {first} till {last} av {totalRecords} träffar"
            paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
            :rows="paginator ? getRowsPerPage() : undefined"
            :rowsPerPageOptions="rowsPerPageOptions"
            :totalRecords="totalRecords"
            :pagination="paginator"
            :stateKey="useState ? name : undefined"
            :first="first"
            :paginatorPosition="paginatorPosition"
            @sort="onSort"
            @page="onPage"
            @update:rows="onRowsPerPage"
        >
            <template
                v-if="(expandableRows && expandToggles) || $slots.header || exportable || togglableColumns"
                #header
            >
                <div class="header">
                    <!-- Header content för tabellen -->
                    <div v-if="$slots.header" class="custom-header">
                        <slot
                            name="header"
                            :expandAll="expandAll"
                            :collapseAll="collapseAll"
                            :selectedRow="selectedValues"
                        />
                    </div>
                    <div
                        v-if="(expandableRows && expandToggles) || exportable || togglableColumns"
                        class="header-right"
                    >
                        <!-- Expandera/minimera alla -->
                        <div v-if="expandableRows && expandToggles" class="expand-toggles">
                            <KButton type="button" @click="expandAll" size="square-icon">+</KButton>
                            <KButton type="button" @click="collapseAll" size="square-icon">-</KButton>
                        </div>
                        <!-- CSV export -->
                        <KButton v-if="exportable" type="button" @click="exportCsv" size="adaptable">CSV</KButton>
                        <!-- Visa/dölj kolumner -->
                        <KAutocomplete
                            v-if="togglableColumns"
                            showAllOption
                            multiple
                            showSelectedCounter
                            autoPosition
                            overrideLabel="Kolumner"
                            ref="toggledColumnsAutocomplete"
                            name="toggledColumns"
                            variant="basic"
                            class="toggled-columns-autocomplete"
                            :options="[]"
                            :uid="`${name}-toggledColumns`"
                            @inputValue="onToggleColumn"
                            @search:blur="onToggleColumnBlur"
                        >
                            <template #list-footer>
                                <KButton type="button" @click="resetColumns" size="small" variant="contour"
                                    >Återställ kolumner</KButton
                                >
                            </template>
                        </KAutocomplete>
                    </div>
                </div>
            </template>
            <!-- Kolumner renderas här -->
            <template #default>
                <!-- Radio button eller checkbox (beroende av selectionMode) för val av rad -->
                <PrimeColumn
                    v-if="showSelectionToggle"
                    :frozen="true"
                    :selectionMode="selectionMode"
                    :styles="{
                        minWidth: `${CellContentWidth.icon}`,
                        maxWidth: `${CellContentWidth.icon}`,
                    }"
                />
                <!-- Expand/collapse-knappen -->
                <!-- Primevue har default 2rem på storleken på chevron -->
                <PrimeColumn
                    v-if="expandableRows && showExpandToggle"
                    :expander="true"
                    :styles="{
                        minWidth: `calc(2rem + ${padding} * 2`,
                        maxWidth: `calc(2rem + ${padding} * 2`,
                    }"
                    :frozen="true"
                />
                <!--
                    Dynamiskt skapade kolumner.
                    index MÅSTE vara med i key för att flytt och visning/döljning av kolumner ska fungera
                -->
                <PrimeColumn
                    v-for="(column, index) of filteredColumns"
                    :key="`${column.field}_${index}`"
                    :field="column.field"
                    :frozen="column.frozen"
                    :alignFrozen="column.alignFrozen ?? 'left'"
                    :exportHeader="column.exportHeader"
                    :hidden="column.hidden"
                    :reorderableColumn="column.frozen ? false : column.reorderableColumn"
                    :styles="{
                        minWidth: getWidth({ index, columns: filteredColumns }),
                        maxWidth: column.limitWidth ? getWidth({ index, columns: filteredColumns }) : undefined,
                    }"
                    :sortable="column.sortable"
                    :sortField="column.sortField"
                >
                    <template #header>
                        <template v-if="column.headerTemplate">
                            <component :is="column.headerTemplate" :data="column.header" />
                        </template>
                        <template v-else>
                            <span>{{ column.header }}</span>
                        </template>
                    </template>
                    <template #body="slotProps">
                        <!-- Fallback till generisk cell om inte template skickats in -->
                        <template v-if="column.bodyTemplate">
                            <component
                                :is="column.bodyTemplate"
                                :data="slotProps?.data?.[slotProps?.field]"
                                :contentType="column.contentType"
                                :alignContent="column.alignContent"
                                :justifyContent="column.justifyContent"
                                :truncate="column.truncate"
                                :lines="column.lines"
                                :contentDirection="column.contentDirection"
                                :tooltipContent="column.tooltipContent"
                            />
                        </template>
                        <template v-else>
                            <KCell
                                :data="slotProps?.data?.[slotProps?.field]"
                                :contentType="column.contentType"
                                :alignContent="column.alignContent"
                                :justifyContent="column.justifyContent"
                                :truncate="column.truncate"
                                :lines="column.lines"
                                :contentDirection="column.contentDirection"
                                :tooltipContent="column.tooltipContent"
                                :booleanShowNo="column.booleanShowNo"
                            />
                        </template>
                    </template>
                    <template #footer="slotProps">
                        <template v-if="column.footerTemplate">
                            <component :is="column.footerTemplate" :data="slotProps?.data?.[slotProps?.field]" />
                        </template>
                    </template>
                </PrimeColumn>
                <!--
                    Kolumn med actionmenyn. Renderas bara om minst 1 rad eller generella actions skickats in.
                    index MÅSTE vara med i key för att låta actions hamna i slutet när kolumner kan flyttas/döljas
                 -->
                <PrimeColumn
                    v-if="hasActions()"
                    :frozen="true"
                    :key="`actions_${filteredColumns.length}`"
                    field="actions"
                    alignFrozen="right"
                    :reorderableColumn="false"
                    :styles="{
                        minWidth: CellContentWidth.custom({ amount: 2 }),
                        maxWidth: CellContentWidth.custom({ amount: 2 }),
                    }"
                >
                    <template #header>
                        <span />
                    </template>
                    <template #body="slotProps">
                        <KlinikenDropdownMenu
                            popperClass="k-table-dropdown"
                            :item="{ ...(slotProps?.data ?? {}), tableIndex: slotProps?.index }"
                            :dropdownActions="slotProps?.data?.actions ?? actions"
                            @show="onToggleDropdown"
                        />
                    </template>
                </PrimeColumn>
            </template>
            <!-- Innehåll i expanderade rader -->
            <template v-if="expandableRows" #expansion="slotProps">
                <div :class="['expanded-row', {}]">
                    <slot name="expandedRow" :data="slotProps?.data" />
                </div>
            </template>
            <template #empty>
                <span>Inga resultat</span>
            </template>
        </PrimeDataTable>
        <div v-if="typeof loading === 'boolean' ? loading : internalLoading" class="loading-message">
            <span v-if="loadingMessage">{{ `${loadingMessage}...` }}</span>
        </div>
    </div>
</template>

<style lang="scss">
    .dropdown-menu {
        &.k-table-dropdown {
            padding: 0;
        }
    }
</style>

<style scoped lang="scss">
    @import "@/style/variables";
    @import "@/style/container";

    .k-table {
        position: relative;
        max-width: 100%;

        .loading-message {
            position: absolute;
            top: 0;
            z-index: 2;
            left: 0;
            right: 0;
            bottom: 0;
            display: grid;
            place-items: center;

            span {
                height: fit-content;
                color: $color-dark;
                background-color: $color-grey-bg;
                border-radius: $border-radius-max;
                padding: 0.5rem;
                box-shadow: $box-shadow-floating-button;
            }
        }

        &.width-default {
            width: fit-content;
        }

        &.width-unlimited {
            width: 100%;
        }

        &.width-max-content {
            width: $content-max-width;
        }

        ::v-deep .p-datatable {
            font-family: Roboto, sans-serif;

            .p-datatable-header {
                background: none;
                border: none;
                padding: 1rem 0;

                .header {
                    display: flex;
                    gap: 1rem;

                    .custom-header {
                        margin-left: auto;
                    }

                    .toggled-columns-autocomplete {
                        max-width: calc(8rem + $icon-gap-default);
                    }
                }
            }

            .p-paginator {
                font-size: 0.9rem;
                padding: 0.5rem;

                .p-paginator-current {
                    padding: 0;
                }

                button,
                .p-dropdown-label,
                .p-dropdown-panel {
                    font-size: 0.9rem;
                }

                .p-dropdown-trigger-icon {
                    &::before {
                        display: inline-block;
                        transform: scale(0.8);
                    }
                }
            }

            &.theme-default {
                .p-datatable-wrapper {
                    .p-datatable-thead th {
                        background: $color-blue-light;
                    }

                    .p-datatable-tbody {
                        tr {
                            &.newly-updated,
                            &.newly-created {
                                background: $color-readonly-background;
                            }

                            &:hover {
                                background: $color-blue-light;
                            }
                        }
                    }
                }
            }

            &.theme-grey {
                .p-datatable-wrapper {
                    .p-datatable-thead th {
                        background: $color-grey-light;
                    }

                    .p-datatable-tbody {
                        tr {
                            background: $color-grey-very-light;

                            &.newly-updated,
                            &.newly-created {
                                background: $color-readonly-background;
                            }

                            &:hover {
                                background: $color-grey-light;
                            }
                        }
                    }
                }
            }

            .p-datatable-wrapper {
                border-radius: $border-radius-max;
                border: 1px solid $list-border-color;

                .p-datatable-table {
                    // Header i tabellen
                    .p-datatable-thead {
                        color: $color-table-header;

                        th {
                            color: inherit;
                            border-color: $list-border-color;
                            padding: v-bind(padding);
                            padding-top: calc(v-bind(padding) * 2);
                            align-items: flex-end;

                            .p-column-header-content {
                                font-size: 0.8rem;
                                font-family: Roboto medium, sans-serif;

                                .p-sortable-column-icon {
                                    cursor: pointer;
                                }
                            }

                            &:not(.p-frozen-column) {
                                cursor: v-bind(headCursor);
                            }

                            .pi-sort-alt:before,
                            .pi-sort-amount-up-alt:before,
                            .pi-sort-amount-down:before {
                                display: inline-block;
                                transform: scale(0.8);
                            }
                        }
                    }

                    // Body i tabellen
                    .p-datatable-tbody {
                        & > tr:not(.p-datatable-row-expansion) {
                            & > td {
                                border-color: $list-border-color;
                                padding: v-bind(padding);

                                > * {
                                }
                            }
                        }

                        // Rad som är valbar
                        .p-selectable-row {
                            &:focus {
                                background-color: $color-blue-light;
                            }

                            // Raden är vald
                            &.p-highlight {
                                background-color: $color-blue-light;

                                // Sätts på första td istället för tr, eftersom hela tr kan sscrollas och sticky +
                                // tabell is a no go
                                td:first-child {
                                    &::before {
                                        content: "";
                                        position: absolute;
                                        width: 4px;
                                        height: 100%;
                                        left: 0;
                                        background-color: $color-default-blue;
                                        z-index: 1;
                                    }
                                }
                            }

                            &:has(+ .p-datatable-row-expansion) {
                                td {
                                    border: 0;
                                }
                            }

                            & + .p-datatable-row-expansion {
                                background-color: $color-blue-light;
                            }
                        }

                        .p-radiobutton {
                            align-items: center;
                        }

                        // Kolumn med chevron för expandering/minimering i Primevue
                        .p-frozen-column:has(.p-row-toggler) {
                            min-width: 1rem !important;
                        }

                        .variant-default {
                        }

                        .variant-warning {
                            background-color: $color-warning;
                        }
                    }

                    // Body och header i tabellen. För sättning av drop shadows på frozen columns
                    .p-datatable-tbody,
                    .p-datatable-thead {
                        & > tr {
                            // Sätt drop shadow på höger sida på frozen column som inte har en frozen kolumn
                            // till höger (i praktiken sista frozen kolumn till vänster)
                            & > .p-frozen-column:not(:has(+ .p-frozen-column)) {
                                box-shadow: $drop-shadow-right;

                                // Lägger till padding till vänster för mer utrymme mot frozen column
                                & + td,
                                & + th {
                                    padding-left: calc(v-bind(padding) * 2);
                                }
                            }

                            // Sätt drop shadow på vänster sida på frozen column som inte har en frozen kolumn
                            // till vänster (i praktiken första frozen kolumn till höger)
                            & > :not(.p-frozen-column) + .p-frozen-column {
                                box-shadow: $drop-shadow-left;
                            }

                            & > :not(.p-frozen-column):has(+ .p-frozen-column) {
                                // Lägger till padding till höger för mer utrymme mot frozen column
                                padding-right: calc(v-bind(padding) * 2);
                            }
                        }
                    }

                    // Footern i tabellen
                    .p-datatable-tfoot {
                        z-index: unset;
                    }
                }
            }

            // Styling av raden med expanderat innehåll
            &.expanded-variant-default {
                .p-datatable-row-expansion {
                    & > td {
                        padding-top: 0;
                    }

                    padding-left: v-bind(selectToggleWidth);
                }
            }

            // Styling av raden med expanderat innehåll
            &.expanded-variant-full-framed {
                .p-datatable-wrapper {
                    .p-datatable-table {
                        tr:has(+ .p-datatable-row-expansion) {
                            &.p-selectable-row {
                                background-color: $color-hover;
                            }
                        }
                    }

                    .p-datatable-row-expansion {
                        border: 6px solid $color-hover;
                        border-top: 0;
                        padding: calc(v-bind(padding) * 2);

                        td {
                            padding: 0;
                        }
                    }
                }
            }
        }

        ::v-deep .p-datatable-loading-overlay {
            display: flex;
            flex-direction: column;
            align-items: center;
            background-color: unset;

            &::before {
                content: "";
                background-color: $color-background-light;
                opacity: 0.5;
                width: 100%;
                height: 100%;
                position: absolute;
                border-radius: $border-radius-max;
            }

            .p-datatable-loading-icon {
                background-image: url("@/assets/loader.svg");
                background-size: cover;
                filter: drop-shadow(0px 0px 2px $color-box-shadow);
                width: 100px;
                height: 100px;
            }
        }
    }
</style>
